import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// ─────────────────────────────────────────────────────────────────────
// Multi-source-document support.
//
// All thunks now accept an optional `docType` parameter (default
// "SalesInvoice") that selects the right BackOffice/EInvoice route.
// Existing call sites that pass a single `salesInvoiceId` keep working
// because `normaliseDocPayload` extracts it from either shape:
//   thunk("ABC")                                            // legacy invoice form
//   thunk({ docType: "SalesInvoice", docId: "ABC" })         // explicit invoice
//   thunk({ docType: "CounterSale",  docId: "XYZ" })         // counter sale
//
// The EWB modal endpoints (generate/cancel-EWB, update-vehicle, extend-validity)
// take an EWayBillRecordId rather than the source-doc id, so they're shared
// across both flows and don't need a docType parameter.
// ─────────────────────────────────────────────────────────────────────

const DOC_TYPE_SALES_INVOICE = "SalesInvoice";
const DOC_TYPE_COUNTER_SALE = "CounterSale";

const resolveGenerateUrl = (docType, docId) =>
  docType === DOC_TYPE_COUNTER_SALE
    ? API_URL.EINVOICE.CS_GENERATE(docId)
    : API_URL.EINVOICE.GENERATE(docId);

const resolveCancelUrl = (docType, docId) =>
  docType === DOC_TYPE_COUNTER_SALE
    ? API_URL.EINVOICE.CS_CANCEL(docId)
    : API_URL.EINVOICE.CANCEL(docId);

// Retry shares the generate URL on the CS path — the orchestrator's
// generate-by-id route self-detects retry-vs-new via the existing-record
// query, so we don't need a dedicated CS retry endpoint.
const resolveRetryUrl = (docType, docId) =>
  docType === DOC_TYPE_COUNTER_SALE
    ? API_URL.EINVOICE.CS_GENERATE(docId)
    : API_URL.EINVOICE.RETRY(docId);

const resolveCombinedGenerateUrl = (docType, docId) =>
  docType === DOC_TYPE_COUNTER_SALE
    ? API_URL.EINVOICE.CS_GENERATE_WITH_EWAYBILL(docId)
    : API_URL.EINVOICE.GENERATE_WITH_EWAYBILL(docId);

// CancelWithEWayBill is currently Invoice-only on the BE (Phase 2 keeps the CS
// cascade narrow to plain IRN cancel). Counter Sale callers fall back to plain
// cancel — same effective behaviour for CS today since the typical CS doesn't
// carry an active EWB linked to the IRN.
const resolveCombinedCancelUrl = (docType, docId) =>
  docType === DOC_TYPE_COUNTER_SALE
    ? API_URL.EINVOICE.CS_CANCEL(docId)
    : API_URL.EINVOICE.CANCEL_WITH_EWAYBILL(docId);

// Backwards-compat: turn a "raw id string" or a "{ docType, docId }" object
// into a normalised pair. Older Invoice call sites that still pass a plain
// salesInvoiceId continue to work without changes.
const normaliseDocPayload = (payload) => {
  if (payload && typeof payload === "object" && "docId" in payload) {
    return { docType: payload.docType || DOC_TYPE_SALES_INVOICE, docId: payload.docId };
  }
  return { docType: DOC_TYPE_SALES_INVOICE, docId: payload };
};

const handleEInvoiceResponse = (response) => {
  const { data } = response;
  // Check outer wrapper isSuccess (ApiBaseResponse)
  if (!data.isSuccess) {
    // data.data contains the inner result with errorMessage
    const error = data.data || {};
    throw { errorCode: error.errorCode, errorMessage: error.errorMessage || data.message || "Operation failed" };
  }
  return data.data;
};

export const generateEInvoice = createAsyncThunk(
  "eInvoice/generate",
  async (payload, { rejectWithValue }) => {
    try {
      const { docType, docId } = normaliseDocPayload(payload);
      const response = await axios.post(resolveGenerateUrl(docType, docId));
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(error.errorMessage ? error : (error.data || error));
    }
  }
);

export const cancelEInvoice = createAsyncThunk(
  "eInvoice/cancel",
  async (payload, { rejectWithValue }) => {
    try {
      // Accepts { salesInvoiceId, reason, remarks } (legacy) OR
      //         { docType, docId, reason, remarks } (new form).
      const { reason, remarks } = payload;
      const { docType, docId } = normaliseDocPayload(
        payload?.docId !== undefined
          ? payload
          : { docType: DOC_TYPE_SALES_INVOICE, docId: payload?.salesInvoiceId },
      );
      const response = await axios.post(resolveCancelUrl(docType, docId), {
        reason,
        remarks,
      });
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(error.errorMessage ? error : (error.data || error));
    }
  }
);

export const retryEInvoice = createAsyncThunk(
  "eInvoice/retry",
  async (payload, { rejectWithValue }) => {
    try {
      const { docType, docId } = normaliseDocPayload(payload);
      const response = await axios.post(resolveRetryUrl(docType, docId));
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(error.errorMessage ? error : (error.data || error));
    }
  }
);

export const generateEInvoiceAndEWayBill = createAsyncThunk(
  "eInvoice/generateCombined",
  async (payload, { rejectWithValue }) => {
    try {
      // Accepts { salesInvoiceId, transportDetails } (legacy) OR
      //         { docType, docId, transportDetails } (new form).
      const transportDetails = payload?.transportDetails;
      const { docType, docId } = normaliseDocPayload(
        payload?.docId !== undefined
          ? payload
          : { docType: DOC_TYPE_SALES_INVOICE, docId: payload?.salesInvoiceId },
      );
      // Single backend call (plan §22). Partial failure (IRN ✓, EWB ✗) is surfaced
      // via `eWayBillErrorCode` / `eWayBillErrorMessage` on a successful response.
      const response = await axios.post(
        resolveCombinedGenerateUrl(docType, docId),
        transportDetails,
      );
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(
        error.errorMessage ? error : error.data || error,
      );
    }
  }
);

export const cancelEInvoiceAndEWayBill = createAsyncThunk(
  "eInvoice/cancelCombined",
  async (payload, { rejectWithValue }) => {
    try {
      const { reason, remarks } = payload;
      const { docType, docId } = normaliseDocPayload(
        payload?.docId !== undefined
          ? payload
          : { docType: DOC_TYPE_SALES_INVOICE, docId: payload?.salesInvoiceId },
      );
      // Invoice path uses combined cancel (plan §13.1 — auto-routes by EWB state).
      // CS path falls through to plain IRN cancel (see resolveCombinedCancelUrl).
      const response = await axios.post(
        resolveCombinedCancelUrl(docType, docId),
        { reason, remarks },
      );
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(
        error.errorMessage ? error : error.data || error,
      );
    }
  }
);

export const generateEWayBill = createAsyncThunk(
  "eInvoice/generateEWayBill",
  async (payload, { rejectWithValue }) => {
    try {
      // Accepts { salesInvoiceId, transportDetails } (legacy) OR
      //         { docType, docId, transportDetails } (new form).
      // CS path uses the dedicated counter-sale/ewaybill/generate route which
      // handles both linked-EWB (when a CSL IRN exists) and standalone-EWB
      // (when it doesn't) cases.
      const transportDetails = payload?.transportDetails;
      const { docType, docId } = normaliseDocPayload(
        payload?.docId !== undefined
          ? payload
          : { docType: DOC_TYPE_SALES_INVOICE, docId: payload?.salesInvoiceId },
      );
      const url =
        docType === DOC_TYPE_COUNTER_SALE
          ? API_URL.EINVOICE.CS_GENERATE_EWAYBILL(docId)
          : API_URL.EINVOICE.GENERATE_EWAYBILL(docId);
      const response = await axios.post(url, transportDetails);
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(error.errorMessage ? error : (error.data || error));
    }
  }
);

export const cancelEWayBill = createAsyncThunk(
  "eInvoice/cancelEWayBill",
  async ({ eWayBillRecordId, reason, remarks }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_URL.EINVOICE.CANCEL_EWAYBILL(eWayBillRecordId),
        { reason, remarks }
      );
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(error.errorMessage ? error : (error.data || error));
    }
  }
);

export const updateEWayBillVehicle = createAsyncThunk(
  "eInvoice/updateVehicle",
  async ({ eWayBillRecordId, details }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_URL.EINVOICE.UPDATE_VEHICLE(eWayBillRecordId),
        details
      );
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(error.errorMessage ? error : (error.data || error));
    }
  }
);

export const extendEWayBillValidity = createAsyncThunk(
  "eInvoice/extendValidity",
  async ({ eWayBillRecordId, details }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_URL.EINVOICE.EXTEND_VALIDITY(eWayBillRecordId),
        details
      );
      return handleEInvoiceResponse(response);
    } catch (error) {
      return rejectWithValue(error.errorMessage ? error : (error.data || error));
    }
  }
);

const eInvoiceSlice = createSlice({
  name: "eInvoice",
  initialState: {
    isGenerating: false,
    isGeneratingCombined: false,
    isCancelling: false,
    isCancellingCombined: false,
    isRetrying: false,
    isGeneratingEWB: false,
    isCancellingEWB: false,
    isUpdatingVehicle: false,
    isExtendingValidity: false,
    showEWayBillModal: false,
    eWayBillModalMode: "standalone", // "standalone" | "combined"
    showUpdateVehicleModal: false,
    showExtendValidityModal: false,
  },
  reducers: {
    openEWayBillModal(state, action) {
      state.showEWayBillModal = true;
      state.eWayBillModalMode = action.payload?.mode || "standalone";
    },
    closeEWayBillModal(state) {
      state.showEWayBillModal = false;
      state.eWayBillModalMode = "standalone";
    },
    openUpdateVehicleModal(state) {
      state.showUpdateVehicleModal = true;
    },
    closeUpdateVehicleModal(state) {
      state.showUpdateVehicleModal = false;
    },
    openExtendValidityModal(state) {
      state.showExtendValidityModal = true;
    },
    closeExtendValidityModal(state) {
      state.showExtendValidityModal = false;
    },
  },
  extraReducers: (builder) => {
    // Generate E-Invoice
    builder.addCase(generateEInvoice.pending, (state) => {
      state.isGenerating = true;
    });
    builder.addCase(generateEInvoice.fulfilled, (state) => {
      state.isGenerating = false;
    });
    builder.addCase(generateEInvoice.rejected, (state) => {
      state.isGenerating = false;
    });

    // Generate E-Invoice + E-Way Bill (Combined)
    builder.addCase(generateEInvoiceAndEWayBill.pending, (state) => {
      state.isGeneratingCombined = true;
    });
    builder.addCase(generateEInvoiceAndEWayBill.fulfilled, (state) => {
      state.isGeneratingCombined = false;
      state.showEWayBillModal = false;
      state.eWayBillModalMode = "standalone";
    });
    builder.addCase(generateEInvoiceAndEWayBill.rejected, (state) => {
      state.isGeneratingCombined = false;
    });

    // Cancel E-Invoice
    builder.addCase(cancelEInvoice.pending, (state) => {
      state.isCancelling = true;
    });
    builder.addCase(cancelEInvoice.fulfilled, (state) => {
      state.isCancelling = false;
    });
    builder.addCase(cancelEInvoice.rejected, (state) => {
      state.isCancelling = false;
    });

    // Cancel E-Invoice + E-Way Bill (Combined)
    builder.addCase(cancelEInvoiceAndEWayBill.pending, (state) => {
      state.isCancellingCombined = true;
    });
    builder.addCase(cancelEInvoiceAndEWayBill.fulfilled, (state) => {
      state.isCancellingCombined = false;
    });
    builder.addCase(cancelEInvoiceAndEWayBill.rejected, (state) => {
      state.isCancellingCombined = false;
    });

    // Retry E-Invoice
    builder.addCase(retryEInvoice.pending, (state) => {
      state.isRetrying = true;
    });
    builder.addCase(retryEInvoice.fulfilled, (state) => {
      state.isRetrying = false;
    });
    builder.addCase(retryEInvoice.rejected, (state) => {
      state.isRetrying = false;
    });

    // Generate E-Way Bill
    builder.addCase(generateEWayBill.pending, (state) => {
      state.isGeneratingEWB = true;
    });
    builder.addCase(generateEWayBill.fulfilled, (state) => {
      state.isGeneratingEWB = false;
      state.showEWayBillModal = false;
    });
    builder.addCase(generateEWayBill.rejected, (state) => {
      state.isGeneratingEWB = false;
    });

    // Cancel E-Way Bill
    builder.addCase(cancelEWayBill.pending, (state) => {
      state.isCancellingEWB = true;
    });
    builder.addCase(cancelEWayBill.fulfilled, (state) => {
      state.isCancellingEWB = false;
    });
    builder.addCase(cancelEWayBill.rejected, (state) => {
      state.isCancellingEWB = false;
    });

    // Update Vehicle
    builder.addCase(updateEWayBillVehicle.pending, (state) => {
      state.isUpdatingVehicle = true;
    });
    builder.addCase(updateEWayBillVehicle.fulfilled, (state) => {
      state.isUpdatingVehicle = false;
      state.showUpdateVehicleModal = false;
    });
    builder.addCase(updateEWayBillVehicle.rejected, (state) => {
      state.isUpdatingVehicle = false;
    });

    // Extend Validity
    builder.addCase(extendEWayBillValidity.pending, (state) => {
      state.isExtendingValidity = true;
    });
    builder.addCase(extendEWayBillValidity.fulfilled, (state) => {
      state.isExtendingValidity = false;
      state.showExtendValidityModal = false;
    });
    builder.addCase(extendEWayBillValidity.rejected, (state) => {
      state.isExtendingValidity = false;
    });
  },
});

export const {
  openEWayBillModal,
  closeEWayBillModal,
  openUpdateVehicleModal,
  closeUpdateVehicleModal,
  openExtendValidityModal,
  closeExtendValidityModal,
} = eInvoiceSlice.actions;

// Re-exported so consumers can use the same constants the slice does.
export const SOURCE_DOCUMENT_TYPES = {
  SALES_INVOICE: DOC_TYPE_SALES_INVOICE,
  COUNTER_SALE: DOC_TYPE_COUNTER_SALE,
};

export default eInvoiceSlice.reducer;
